// src/pages/clab-unselected-workers/index.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import HeaderBar from "components/ui/HeaderBar";
import RoleBasedSidebar from "components/ui/RoleBasedSidebar";
import BreadcrumbNavigation from "components/ui/BreadcrumbNavigation";
import Icon from "components/AppIcon";
import Image from "components/AppImage";
import { useAuth } from "context/AuthContext";
import { getUserRoleForComponents } from "utils/authHelpers";
import BiodataDetailsView from "components/BiodataDetailsView";
import { biodataAPI } from "utils/api";
import { useBiodataCache } from "context/BiodataCacheContext";
import PaginationComponent from "components/PaginationComponent";
import Notifier from "components/ui/Notifier";

const ClabUnselectedWorkers = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const userRole = getUserRoleForComponents(user);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [biodataList, setBiodataList] = useState([]);
  const [biodataPagination, setBiodataPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: "",
    nationality: "",
    vdr_type: "",
    skill: "",
  });
  const [printLoading, setPrintLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBiodataDetails, setSelectedBiodataDetails] = useState(null);
  const [notification, setNotification] = useState({
    show: false,
    type: "info",
    message: "",
  });
  const {
    getBiodataByStatus,
    invalidateOnUpdate,
    getPhotoUrl,
    prefetchPhotoUrl,
  } = useBiodataCache();

  // Get unique nationalities and VDR types for filters
  const uniqueNationalities = useMemo(() => {
    const nationalities = new Set(
      biodataList.map((b) => b.nationality).filter(Boolean)
    );
    return Array.from(nationalities).sort();
  }, [biodataList]);

  const uniqueVdrTypes = useMemo(() => {
    const types = new Set(biodataList.map((b) => b.vdr_type).filter(Boolean));
    return Array.from(types).sort();
  }, [biodataList]);

  useEffect(() => {
    let isMounted = true;

    const loadUnselectedBiodata = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = {
          page: currentPage,
          per_page: itemsPerPage,
          ...filters,
        };

        const result = await getBiodataByStatus("unselected", params);

        if (result.fromCache) {
        }

        if (isMounted) {
          setBiodataList(result.records || []);
          setBiodataPagination(result.pagination || null);
        }
      } catch (error) {
        if (isMounted) {
          setError("Failed to load unselected biodata");
          console.error("Error loading unselected biodata:", error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadUnselectedBiodata();

    return () => {
      isMounted = false;
    };
  }, [filters, currentPage, itemsPerPage]);

  // When filters change, reset to first page
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // When items per page changes, reset to first page
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  // Progressive photo prefetch when avatars enter viewport
  useEffect(() => {
    const elements = document.querySelectorAll("[data-biodata-id]");
    if (!elements || elements.length === 0) return;
    const seen = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const idAttr = entry.target.getAttribute("data-biodata-id");
          const id = Number(idAttr) || idAttr;
          if (!id || seen.has(id)) return;
          const rec = biodataList.find((b) => b.id === id);
          const direct = rec?.photo_url || rec?.primary_photo_url;
          const cached = getPhotoUrl(id, rec);
          if (!direct && !cached) prefetchPhotoUrl(id);
          seen.add(id);
          observer.unobserve(entry.target);
        });
      },
      { root: null, threshold: 0.25 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [biodataList, getPhotoUrl, prefetchPhotoUrl]);

  // Redirect if not CLAB user
  useEffect(() => {
    if (
      userProfile &&
      !["clab_user", "clab", "admin", "master_admin", "1", "0", 1, 0].includes(
        userProfile.role
      )
    ) {
      navigate("/login-screen");
    }
  }, [userProfile, navigate]);

  const handleViewDetails = async (biodata) => {
    try {
      const result = await biodataAPI.getBiodataById(biodata.id);
      setSelectedBiodataDetails(result.data);
      setShowDetailsModal(true);
    } catch (error) {
      setError("Failed to load biodata details");
    }
  };

  const handleExport = async () => {
    try {
      setNotification({
        show: true,
        type: "info",
        message: "Preparing export...",
      });

      const token = localStorage.getItem("authToken");
      const API_BASE_URL =
        import.meta.env.VITE_API_BASE_URL || "http://localhost:3001";

      const params = new URLSearchParams({
        page: "1",
        per_page: "10000", // Get all records
        ...(filters.search && { search: filters.search }),
        ...(filters.nationality && { nationality: filters.nationality }),
        ...(filters.vdr_type && { vdr_type: filters.vdr_type }),
      });

      const response = await fetch(
        `${API_BASE_URL}/clab/unselected-biodata?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch data for export");
      }

      const result = await response.json();
      const data = result.data || [];

      // Convert to CSV
      const headers = [
        "Worker ID",
        "Full Name",
        "Passport No",
        "Nationality",
        "Gender",
        "Age",
        "VDR Type",
        "Medical Expiry",
        "Recruiter",
        "Created Date",
      ];
      const csvRows = [headers.join(",")];

      data.forEach((biodata) => {
        const row = [
          biodata.worker_id || "",
          biodata.full_name || "",
          biodata.passport_no || "",
          biodata.nationality || "",
          biodata.gender || "",
          biodata.age || "",
          biodata.vdr_type || "",
          biodata.medical_expiry_date
            ? new Date(biodata.medical_expiry_date).toLocaleDateString()
            : "",
          biodata.recruiter?.name || "",
          biodata.created_at
            ? new Date(biodata.created_at).toLocaleDateString()
            : "",
        ];
        csvRows.push(
          row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(",")
        );
      });

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `unselected_workers_${
        new Date().toISOString().split("T")[0]
      }.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setNotification({
        show: true,
        type: "success",
        message: `Exported ${data.length} records`,
      });
    } catch (error) {
      console.error("Export failed:", error);
      setNotification({
        show: true,
        type: "error",
        message: "Export failed: " + error.message,
      });
    }
  };

  const handleBulkPrint = async (printAll = false) => {
    const token = localStorage.getItem("authToken");
    const API_BASE_URL =
      import.meta.env.VITE_API_BASE_URL || "http://localhost:3001";

    if (!token) {
      setNotification({
        show: true,
        type: "error",
        message: "Authentication token not found. Please log in again.",
      });
      return;
    }

    try {
      setPrintLoading(true);
      let ids = [];

      if (printAll) {
        setNotification({
          show: true,
          type: "info",
          message: "Fetching all unselected biodata...",
        });

        const perPage = 1000;
        let pageToFetch = 1;
        let totalPages = 1;

        do {
          const params = new URLSearchParams({
            page: pageToFetch.toString(),
            per_page: perPage.toString(),
          });

          const response = await fetch(
            `${API_BASE_URL}/clab/unselected-biodata?${params}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error(
              `Failed to fetch unselected biodata page ${pageToFetch}`
            );
          }

          const result = await response.json();
          const records = result.data || [];
          ids = ids.concat(records.map((record) => record.id));
          totalPages = result.pagination?.total_pages || 1;
          pageToFetch += 1;
        } while (pageToFetch <= totalPages);

        if (ids.length === 0) {
          setNotification({
            show: true,
            type: "warning",
            message: "No unselected biodata found for printing.",
          });
          return;
        }
      } else {
        if (biodataList.length === 0) {
          setNotification({
            show: true,
            type: "warning",
            message: "No biodata to print on the current page.",
          });
          return;
        }
        ids = biodataList.map((b) => b.id);
      }

      const response = await fetch(`${API_BASE_URL}/biodatas/bulk-print`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ids,
          format: "zip",
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to generate bulk print.");
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error(
          "Received empty file from server. Please try again later."
        );
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().split("T")[0];
      link.href = url;
      link.download = `unselected_biodata_${timestamp}_${ids.length}_records.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setNotification({
        show: true,
        type: "success",
        message: `Generated ${ids.length} biodata files. Extract the ZIP and open in your browser to print.`,
      });
    } catch (error) {
      console.error("Bulk print failed:", error);
      setNotification({
        show: true,
        type: "error",
        message: "Bulk print failed: " + (error.message || "Unknown error"),
      });
    } finally {
      setPrintLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <HeaderBar
          userRole={userRole}
          userName={userProfile?.full_name || "CLAB User"}
          onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <RoleBasedSidebar
          userRole={userRole}
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <main
          className={`transition-all duration-300 pt-16 ${
            sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
          }`}
        >
          <div className="p-6 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Icon
                name="Loader2"
                size={48}
                className="animate-spin text-primary mx-auto mb-4"
              />
              <p className="text-text-secondary">
                Loading unselected workers...
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Notifier
        type={notification.type}
        message={notification.message}
        show={notification.show}
        onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        duration={3000}
      />
      <HeaderBar
        userRole={userRole}
        userName={userProfile?.full_name || "CLAB User"}
        onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <RoleBasedSidebar
        userRole={userRole}
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main
        className={`transition-all duration-300 pt-16 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-6">
          <BreadcrumbNavigation userRole={userRole} />

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-text-primary mb-2">
              Unselected Workers
            </h1>
            <p className="text-text-secondary">
              View approved biodata not yet selected by any employer
            </p>
          </div>

          {error && (
            <div className="bg-error-50 border border-error-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <h3 className="font-semibold text-error-800">Error</h3>
              </div>
              <p className="text-error-700 mt-2">{error}</p>
              <button
                onClick={() => setError(null)}
                className="mt-3 text-sm text-error-600 hover:text-error-800 font-medium"
              >
                Dismiss
              </button>
            </div>
          )}

          <div className="space-y-6">
            {/* Filter Panel */}
            <div className="card p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Search
                  </label>
                  <input
                    type="text"
                    placeholder="Name, passport, worker ID..."
                    value={filters.search}
                    onChange={(e) =>
                      setFilters({ ...filters, search: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:ring-2 focus:ring-primary-300 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Nationality
                  </label>
                  <select
                    value={filters.nationality}
                    onChange={(e) =>
                      setFilters({ ...filters, nationality: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:ring-2 focus:ring-primary-300 focus:border-transparent"
                  >
                    <option value="">All Nationalities</option>
                    {uniqueNationalities.map((nat) => (
                      <option key={nat} value={nat}>
                        {nat}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    VDR Type
                  </label>
                  <select
                    value={filters.vdr_type}
                    onChange={(e) =>
                      setFilters({ ...filters, vdr_type: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:ring-2 focus:ring-primary-300 focus:border-transparent"
                  >
                    <option value="">All VDR Types</option>
                    {uniqueVdrTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Skill keyword
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. welding, scaffolding"
                    value={filters.skill}
                    onChange={(e) =>
                      setFilters({ ...filters, skill: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:ring-2 focus:ring-primary-300 focus:border-transparent"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() =>
                      setFilters({
                        search: "",
                        nationality: "",
                        vdr_type: "",
                        skill: "",
                      })
                    }
                    className="w-full px-4 py-2 text-secondary-700 bg-surface border border-secondary-300 rounded-md hover:bg-secondary-50 transition-colors duration-150"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Biodata Table */}
            <div className="card">
              <div className="p-6 border-b border-secondary-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-text-primary">
                      Unselected Workers (
                      {biodataPagination ? biodataPagination.total : 0})
                    </h2>
                    <p className="text-sm text-text-secondary mt-1">
                      Approved biodata available for employer selection
                    </p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex flex-col items-end space-y-3">
                      <div className="flex flex-wrap items-center justify-end gap-3">
                        <button
                          onClick={() => handleBulkPrint(true)}
                          disabled={printLoading || loading}
                          className="flex items-center space-x-2 px-4 py-2 text-white bg-primary border border-primary-600 rounded-md hover:bg-primary-700 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Icon name="Printer" size={16} />
                          <span>
                            {printLoading
                              ? "Preparing..."
                              : `Print All (${biodataPagination?.total || 0})`}
                          </span>
                        </button>
                        <button
                          onClick={() => handleBulkPrint(false)}
                          disabled={
                            printLoading || loading || biodataList.length === 0
                          }
                          className="flex items-center space-x-2 px-4 py-2 text-primary bg-primary-50 border border-primary-300 rounded-md hover:bg-primary-100 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Icon name="FileText" size={16} />
                          <span>Print Page ({biodataList.length})</span>
                        </button>
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-3">
                        <button
                          onClick={handleExport}
                          disabled={biodataList.length === 0}
                          className="flex items-center space-x-2 px-4 py-2 text-secondary-700 bg-surface border border-secondary-300 rounded-md hover:bg-secondary-50 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Icon name="Download" size={16} />
                          <span>Export CSV</span>
                        </button>
                        <button
                          onClick={() => window.location.reload()}
                          className="flex items-center space-x-2 px-4 py-2 text-secondary-700 bg-surface border border-secondary-300 rounded-md hover:bg-secondary-50 transition-colors duration-150"
                        >
                          <Icon name="RefreshCw" size={16} />
                          <span>Refresh</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {biodataList.length === 0 ? (
                <div className="p-12 text-center">
                  <Icon
                    name="UserX"
                    size={48}
                    className="text-secondary-300 mx-auto mb-4"
                  />
                  <h3 className="text-lg font-medium text-text-primary mb-2">
                    No Unselected Workers
                  </h3>
                  <p className="text-text-secondary">
                    All approved workers have been selected by employers.
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-secondary-50 border-b border-secondary-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Worker No
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Passport No
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Nationality
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Skills
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            VDR Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Medical Expiry
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Recruiter
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-surface divide-y divide-secondary-200">
                        {biodataList.map((biodata) => (
                          <tr
                            key={biodata.id}
                            className="hover:bg-secondary-50 transition-colors duration-150"
                          >
                            <td className="px-6 py-4 font-mono text-xs">
                              {biodata.worker_id || "-"}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-3">
                                <div
                                  className="w-10 h-10 rounded-full overflow-hidden bg-secondary-100"
                                  data-biodata-id={biodata.id}
                                >
                                  <Image
                                    src={getPhotoUrl(biodata.id, biodata)}
                                    alt={biodata.full_name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-text-primary">
                                    {biodata.full_name}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-xs">
                              {biodata.passport_no || "-"}
                            </td>
                            <td className="px-6 py-4 text-xs">
                              {biodata.nationality || "-"}
                            </td>
                            <td className="px-6 py-4 text-xs">
                              {Array.isArray(biodata.skills)
                                ? biodata.skills.join(", ")
                                : biodata.skills || biodata.skill || "-"}
                            </td>
                            <td className="px-6 py-4">
                              {biodata.vdr_type && (
                                <span className="inline-flex items-center px-2 py-1 bg-accent-100 text-accent-800 rounded-full text-xs font-medium">
                                  <Icon
                                    name="Briefcase"
                                    size={12}
                                    className="mr-1"
                                  />
                                  {biodata.vdr_type}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-xs">
                              {biodata.medical_expiry_date
                                ? new Date(
                                    biodata.medical_expiry_date
                                  ).toLocaleDateString()
                                : "-"}
                            </td>
                            <td className="px-6 py-4 text-xs">
                              {biodata.recruiter?.name || "-"}
                            </td>
                            <td className="px-6 py-4">
                              <button
                                onClick={() => handleViewDetails(biodata)}
                                className="p-2 text-accent hover:bg-accent-50 rounded-md transition-colors duration-150"
                                title="View Details"
                              >
                                <Icon name="Eye" size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Component */}
                  {biodataPagination && (
                    <div className="px-6 py-4 border-t border-secondary-200">
                      <PaginationComponent
                        pagination={{
                          currentPage:
                            biodataPagination.current_page || currentPage,
                          perPage: biodataPagination.per_page || itemsPerPage,
                          total: biodataPagination.total || 0,
                          totalPages: biodataPagination.total_pages || 1,
                          from: biodataPagination.from || 1,
                          to: biodataPagination.to || 0,
                        }}
                        onPageChange={setCurrentPage}
                        onPerPageChange={setItemsPerPage}
                        showPerPageSelector={true}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Biodata Details Modal */}
      {showDetailsModal && selectedBiodataDetails && (
        <div className="fixed inset-0 z-1001 bg-secondary-900 bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-secondary-200 flex items-center justify-between">
              <h2 className="text-xl font-bold">Worker Biodata Details</h2>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedBiodataDetails(null);
                }}
                className="text-secondary-600 hover:text-secondary-900"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-6">
              <BiodataDetailsView biodata={selectedBiodataDetails} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClabUnselectedWorkers;
